import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { InputRadioTagTable } from '../../../../components/display/tables/InputRadioTagTable';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';

// Components Styled
import { ContainerButton, Container } from '../components/MainStyledComponents';
import { useAsyncValue, useNavigate, useRevalidator } from 'react-router-dom';
import { dataTypes } from '../../../../components/display/tables/InputRadioTagTable/types';
import { formOrderNaming } from '../../../../components/forms/NewWorkOrderForm/constants';

// GQL
import { client } from '../../../../gql/client';
import { GET_GENERIC_ORDERS } from '../../../../gql/queries/genericOrders';

import { selectAllOrders } from '../../../../gql/selectors/orders';

// Types
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { useDispatch } from 'react-redux';
import { store } from '../../../../state/configureStore';
import { usePollingRevalidation } from '../../../../hooks/usePollingRevalidation';

export default function DuplicateOrderPage(): JSX.Element {
  const { t } = useTranslation(['workOrders', 'pages']);
  const data = useAsyncValue() as dataTypes;
  const revalidator = useRevalidator();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedRowId, setSelectedRowId] = useState<string>('');

  const handleSeeCommunication = useCallback(() => {
    sessionStorage.removeItem(formOrderNaming.NEW_DUPLICATE_WORK_ORDER_FORM);
    navigate(`summary/${selectedRowId}`);
  }, [selectedRowId]);

  const handleDuplicate = useCallback(() => {
    sessionStorage.removeItem(formOrderNaming.NEW_DUPLICATE_WORK_ORDER_FORM);
    navigate(`new-order-duplicate/${selectedRowId}`);
  }, [selectedRowId]);

  const onReFetchData = useCallback(async () => {
    const dataSelector = getCursor(tableIds.DUPLICATE_GENERIC_ORDER)(store.getState());

    // Tarea #11: respetar el take guardado en cursor (page-size selector).
    // Espejo de WorkOrdersPage/DuplicateComunication — sin esto el refetch
    // pedía siempre takeBase (25) aunque el selector estuviera en 50/100.
    const effectiveTake = firstPageTake(dataSelector?.take, takeBase);
    const { data } = await client.query({
      query: GET_GENERIC_ORDERS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          concessionId: dataSelector.concessionId,
          teamIds: dataSelector.teamIds ?? [],
          sortField: dataSelector.sortField,
          orderBy: dataSelector.orderBy ?? 'desc',
          toDate: dataSelector.toDate,
          fromDate: dataSelector.fromDate,
          indexType: dataSelector.indexType,
          indexExpedition: dataSelector.indexExpedition,
          classification: dataSelector.classification,
          predefinedOrderId: dataSelector.predefinedOrderId,
          capitolId: dataSelector.capitolId,
          subCapitolId: dataSelector.subCapitolId,
          states: dataSelector.states,
          pkInitKm: dataSelector.pkInitKm,
          pkInitMeter: dataSelector.pkInitMeter,
          pkEndKm: dataSelector.pkEndKm,
          pkEndMeter: dataSelector.pkEndMeter,
          track: dataSelector.track,
        },
      },
      fetchPolicy: 'network-only',
    });

    // sortField/orderBy se omiten del setCursorData: el reducer hace MERGE y los
    // preserva del cursor actual (evita pisarlos con un hardcode).
    const setCursorData = {
      take: effectiveTake,
      cursor: '',
      concessionId: dataSelector?.concessionId,
      teamIds: dataSelector?.teamIds ?? [],
      fromDate: dataSelector?.fromDate,
      toDate: dataSelector?.toDate,
      indexType: dataSelector?.indexType,
      indexExpedition: dataSelector?.indexExpedition,
      classification: dataSelector?.classification,
      predefinedOrderId: dataSelector?.predefinedOrderId,
      capitolId: dataSelector?.capitolId,
      subCapitolId: dataSelector?.subCapitolId,
      states: dataSelector?.states,
      pkInitKm: dataSelector?.pkInitKm,
      pkInitMeter: dataSelector?.pkInitMeter,
      pkEndKm: dataSelector?.pkEndKm,
      pkEndMeter: dataSelector?.pkEndMeter,
      track: dataSelector?.track,
    };

    dispatch(setCursor(tableIds.DUPLICATE_GENERIC_ORDER, setCursorData));
    await selectAllOrders(data.genericOrders.genericOrders, true);

    revalidator.revalidate();
  }, []);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, []);

  // Polling: refresca datos de pág 1 en cache (network-only) sin cambiar posición del usuario
  const onPollingRefresh = useCallback(async () => {
    const dataSelector = getCursor(tableIds.DUPLICATE_GENERIC_ORDER)(store.getState());
    const effectiveTake = firstPageTake(dataSelector?.take, takeBase);
    await client.query({
      query: GET_GENERIC_ORDERS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          concessionId: dataSelector.concessionId,
          teamIds: dataSelector.teamIds ?? [],
          sortField: dataSelector.sortField,
          orderBy: dataSelector.orderBy ?? 'desc',
          toDate: dataSelector.toDate,
          fromDate: dataSelector.fromDate,
          indexType: dataSelector.indexType,
          indexExpedition: dataSelector.indexExpedition,
          classification: dataSelector.classification,
          predefinedOrderId: dataSelector.predefinedOrderId,
          capitolId: dataSelector.capitolId,
          subCapitolId: dataSelector.subCapitolId,
          states: dataSelector.states,
          pkInitKm: dataSelector.pkInitKm,
          pkInitMeter: dataSelector.pkInitMeter,
          pkEndKm: dataSelector.pkEndKm,
          pkEndMeter: dataSelector.pkEndMeter,
          track: dataSelector.track,
        },
      },
      fetchPolicy: 'network-only',
    });
    revalidator.revalidate();
  }, []);
  usePollingRevalidation(onPollingRefresh);
  return (
    <Container>
      <InputRadioTagTable
        titleText={t('pages:workOrders.duplicate')}
        setRowState={setSelectedRowId}
        data={data}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        tableId={tableIds.DUPLICATE_GENERIC_ORDER}
        // La tabla es routing-agnóstica (2026-07-09): el doble-click a
        // /work-orders/:id lo aporta el consumidor (antes era el default
        // interno de la tabla vía useNavigate; se retiró para poder usarla
        // en modales fuera del <Router>).
        onRowDoubleTap={(row) => {
          if (row.original.id) navigate(`/work-orders/${row.original.id}`);
        }}
      />
      <ContainerButton>
        <MainButton
          variant="secondary"
          text={t('pages:workOrders.view')}
          onClick={handleSeeCommunication}
          disabled={selectedRowId.length === 0}
        />
        <MainButton
          text={t('pages:workOrders.duplicateOT')}
          onClick={handleDuplicate}
          disabled={selectedRowId.length === 0}
        />
      </ContainerButton>
    </Container>
  );
}
